import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Alert, Box, Button, ChoiceTile, Divider, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { IntegrationsPath } from 'entities/ExportRouters'
import { TeamsSelectField } from 'pages/clients/ui/shared/TeamsSelectField/ui/TeamsSelectField'
import { DoubleOptInSettingLayout } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSetting/DoubleOptInSettingLayout'
import { ClientInviteField } from 'pages/clients/pages/create/ui/ClientDetails/ClientInviteField/ClientInviteField'
import { useCreateContext } from 'pages/clients/pages/create/context/createContext'
import styles from './styles.module.scss'
import { DuplicateField } from './DuplicateField/DuplicateField'

export const ClientDetails = observer(() => {
  const { isGroupsFlowEnabled, clientDetailsStore, nextStep } = useCreateContext()
  const {
    createOptions,
    selectedType,
    setSelectedType,
    useExistingIntegration,
    setUseExistingIntegration,
    integrationSelectItems,
    selectedIntegrationItem,
    setSelectedIntegration,
    isHubSpotSelected,
    activateRefresh,
    isRefreshLoading,
    teamsSelectFieldStore,
    hasNoConnectedIntegration,
    init,
    validate,
    validateLoading,
    isCreateTypeShouldShow,
    isDuplicateType,
  } = clientDetailsStore

  useEffect(() => {
    void init()
  }, [])

  return (
    <Box marginTop='36px'>
      <Typography ariaLabel='clientDetailsTitle' variant='heading-lg'>
        Client details
      </Typography>
      {isGroupsFlowEnabled && (
        <DoubleOptInSettingLayout
          title={'Use existing integration'}
          description={
            'When enabled, your new client will use your current integrated account. Turn this off to connect a different account.'
          }
          checked={useExistingIntegration}
          onToggle={setUseExistingIntegration}
          className={styles.toggleItem}
        />
      )}
      {useExistingIntegration && (
        <>
          {hasNoConnectedIntegration && (
            <Alert
              marginTop={8}
              item={{
                type: 'warning',
                desc: 'No integration connected yet',
              }}
              rightActions={[
                {
                  variant: 'navLink',
                  type: 'primary',
                  text: 'Set up integration',
                  link: `/${IntegrationsPath.root}`,
                  appType: 'react',
                },
              ]}
            />
          )}
          {!hasNoConnectedIntegration && (
            <Box display='flex' flexDirection='column' gap='12px' marginTop='12px'>
              <SelectField
                labelProps={{
                  label: {
                    text: 'Integration',
                    rightNode: isHubSpotSelected ? (
                      <Button
                        text={'Refresh data'}
                        typeBtn='link'
                        contained='primary'
                        onClick={activateRefresh}
                        disabled={isRefreshLoading}
                        size='small'
                      />
                    ) : undefined,
                  },
                }}
                items={integrationSelectItems}
                item={selectedIntegrationItem}
                setValue={setSelectedIntegration}
                placeholder='Select integration'
                disabled={isRefreshLoading}
                withSearch={false}
                selectProps={{
                  variant: 'stroke',
                  textFieldPlaceholderProps: {
                    size: 'medium',
                  },
                }}
              />
              {isHubSpotSelected && (
                <TeamsSelectField
                  store={teamsSelectFieldStore}
                  labelText='HubSpot teams'
                  width={492}
                  placeholderClassName={styles.teamsPlaceholder}
                  disabled={isRefreshLoading}
                />
              )}
            </Box>
          )}
        </>
      )}
      <Divider
        style={{ marginTop: useExistingIntegration ? '32px' : '16px', marginBottom: '24px' }}
      />
      {isCreateTypeShouldShow && (
        <>
          <Typography
            ariaLabel='createClientTitle'
            variant='body-md-regular'
            color='var(--content-primary-secondary)'
            tag='div'
            marginBottom={12}
          >
            How do you want to create the client
          </Typography>
          <div className={styles.cardsContainer}>
            {createOptions.map((option) => (
              <ChoiceTile
                className={styles.choiceTile}
                key={option.value}
                iconProps={{
                  icon: option.icon,
                  fontSize: 24,
                  color:
                    selectedType === option.value
                      ? 'var(--content-brand-primary)'
                      : 'var(--content-primary-tertiary)',
                }}
                title={option.title}
                isSimleIcon
                value={option.value}
                checked={selectedType === option.value}
                setChoiceTile={(value) => setSelectedType(value)}
                titleProps={{
                  ariaLabel: 'ChoiceTile',
                  variant: 'body-md-regular',
                }}
              />
            ))}
          </div>
        </>
      )}
      {isDuplicateType && <DuplicateField />}
      <Box marginTop='36px'>
        <Typography
          ariaLabel='clientInviteFieldTitle'
          variant='text-input-label-small'
          tag='div'
          color='var(--content-primary-secondary)'
          marginBottom={8}
        >
          Invite to organization
        </Typography>
        <ClientInviteField />
      </Box>
      <div className={styles.action}>
        <Button
          text='Next'
          typeBtn='contained'
          fullWidth
          size='large'
          contained='primary'
          onClick={() => validate(nextStep)}
          disabled={validateLoading}
        />
      </div>
    </Box>
  )
})
